import React, { forwardRef, useId } from 'react';
import classNames from 'classnames';
import { Caption, Text } from '@/design-system/components/Basic/Typography';

export interface TextAreaFieldProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className'
> {
  className?: string;
  wrapperClassName?: string;
  label?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  cols?: number;
  name?: string;
  maxLength?: number;
}

const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  (
    {
      className,
      wrapperClassName,
      label,
      value,
      onChange,
      onBlur,
      error,
      disabled,
      required,
      placeholder,
      rows = 3,
      cols,
      name,
      maxLength,
      id,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const textareaId = id ?? `textarea-${generatedId.replace(/:/g, '-')}`;
    const isControlled = value !== undefined;
    const length = (value ?? '').length;

    return (
      <div className={classNames('ds-input-wrapper flex flex-col gap-3', wrapperClassName)}>
        {label && (
          <Text as="label" size="s" weight="semibold" htmlFor={textareaId}>
            {label}
            {required && ' *'}
          </Text>
        )}
        <div className="container-gradient-border rounded-xl">
          <textarea
            ref={ref}
            id={textareaId}
            required={required}
            disabled={disabled}
            aria-invalid={!!error}
            aria-describedby={
              error
                ? `${textareaId}-error`
                : maxLength != null
                  ? `${textareaId}-counter`
                  : undefined
            }
            className={classNames(
              'ds-input-trigger-base input-border-gradient resize-none px-3.5 py-2.5 h-full',
              length > 0 && 'filled',
              className,
            )}
            {...(isControlled ? { value } : {})}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            rows={rows}
            cols={cols}
            maxLength={maxLength}
            {...rest}
          />
        </div>
        <div className="flex gap-3 justify-end px-1">
          {error && (
            <Caption as="p" className="flex-1 text-ds-text-feedback-error">
              {error}
            </Caption>
          )}

          {maxLength != null && (
            <Caption as="p" className="text-ds-text-4 ml-auto" aria-live="polite">
              {length} / {maxLength}
            </Caption>
          )}
        </div>
      </div>
    );
  },
);

TextAreaField.displayName = 'TextAreaField';

export { TextAreaField };
